import DsMatchScouting from "@/components/DsScouting/Match";
import {dsOptional} from "@/utils/dsContext";
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import ScoreRing from "@/components/ScoreRing";
import {
	DsChips,
	DsEmpty,
	DsKpi,
	DsKpiRow,
	DsMissing,
	DsSection,
	DsSkeleton,
	DsStatus,
} from "@/components/DsKit";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {matchMetricValue} from "@/utils/computePlayerStats";
import {
	compareMatchEvents,
	matchEventTime,
	matchEventBelongsToSide,
} from "@/utils/matchEvents";
import {MatchAPI} from "@/api/match";
import {useClubSnapshot} from "@/utils/hooks";
import {
	matchGoals,
	summarizeDsGps,
	playerName,
	hasMatchScore,
	isMatchScheduled,
	matchResultWarning,
	matchDateLabel,
	matchCountdown,
} from "@/utils/clubSnapshot";
import {ERROR_OPERATION} from "@/utils/constants";

// Sintesi della gara: un blocco per ogni tab della scheda completa che ha dati,
// e una riga unica per quelle ancora vuote.

const RESULT_SOURCE = {
	events: "dalla cronaca",
	manual: "inserito a mano",
	conflict: "da confrontare",
	uncollected: "da completare",
};

const KEY_EVENTS = {
	goal: "Gol",
	penalty_scored: "Rigore",
	own_goal: "Autogol",
	penalty_missed: "Rigore sbagliato",
	red_card: "Espulsione",
	double_yellow_card: "Doppio giallo",
	injury_exit: "Uscita per infortunio",
};

const CONVOCATION_STATUSES = [
	"Convocato",
	"Presente",
	"Assente",
	"Infortunato",
];

const fmt = (v, decimals = 0) =>
	v === null || v === undefined || isNaN(v)
		? "—"
		: Number(v).toLocaleString("it-IT", {maximumFractionDigits: decimals});

const metric = (row, key) => matchMetricValue({statistics: row}, key);

function DsMatchPage(props) {
	const {handleNotification} = props;
	const {id} = useParams();
	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);
	const {
		data,
		loading: snapshotLoading,
		error: snapshotError,
	} = useClubSnapshot({handleNotification});
	const full = data?.full || data;

	const [selectedSide, setSelectedSide] = useState(null);
	const [detail, setDetail] = useState(null);
	const [detailLoading, setDetailLoading] = useState(true);

	useEffect(() => {
		let active = true;
		const load = async () => {
			setDetailLoading(true);
			setDetail(null);
			setSelectedSide(null);
			try {
				const results = await Promise.allSettled([
					MatchAPI.getById(id, {seasonId}),
					MatchAPI.getPlayerStatistics(id),
					MatchAPI.getNotes(id, {seasonId}),
					dsOptional(MatchAPI.getAnalysisById(id), null),
				]);
				if (results[0].status === "rejected") throw results[0].reason;
				if (active)
					setDetail({
						match: results[0].value,
						stats:
							results[1].status === "fulfilled" ? results[1].value || [] : null,
						statsError: results[1].status === "rejected",
						notes:
							results[2].status === "fulfilled" ? results[2].value || [] : [],
						notesError: results[2].status === "rejected",
						analysis:
							results[3].status === "fulfilled" ? results[3].value : null,
						analysisError: results[3].status === "rejected",
					});
			} catch {
				if (active) {
					setDetail(null);
					handleNotification(ERROR_OPERATION, "error");
				}
			} finally {
				if (active) setDetailLoading(false);
			}
		};
		load();
		return () => {
			active = false;
		};
	}, [id, seasonId]); // eslint-disable-line react-hooks/exhaustive-deps

	const match = detail?.match;
	const sides = ["home", "away"].filter(
		(value) =>
			match &&
			(!match.participationSides || match.participationSides.includes(value)),
	);
	const ownSides = sides.filter((value) =>
		full?.teams?.some((t) => t.id === match[value + "RosterId"]),
	);
	const defaultSide =
		ownSides.length === 1 ? ownSides[0] : sides.length === 1 ? sides[0] : null;
	const side = sides.includes(selectedSide) ? selectedSide : defaultSide;
	const team = full?.teams?.find(
		(t) => side && t.id === match?.[side + "RosterId"],
	);
	const sideLabel = (value) =>
		(value === "home" ? "Casa · " : "Trasferta · ") +
		(match?.[value + "TeamName"] || match?.[value + "Club"]);
	const rowSide = (row) => {
		if (row.matchSide)
			return ["home", "away"].includes(row.matchSide) ? row.matchSide : null;
		if (!row.clubId || !match?.homeClubId || !match?.awayClubId) return null;
		return (
			["home", "away"].find((value) =>
				matchEventBelongsToSide(row, match, value),
			) || null
		);
	};
	const ours = (row) => Boolean(side && rowSide(row) === side);
	const [homeGoals, awayGoals] = match ? matchGoals(match) : [null, null];
	const hasResult = match && hasMatchScore(match);
	const resultWarning = match && matchResultWarning(match);
	const isPast = match && !isMatchScheduled(match);
	const playersById = new Map((team?.players || []).map((p) => [p.id, p]));
	const nameOf = (pid, fallback) =>
		playersById.get(pid) ? playerName(playersById.get(pid)) : fallback || "—";

	// Convocazioni e formazione
	const convocations = (match?.matchConvocations || []).filter(ours);
	const byStatus = Object.fromEntries(
		CONVOCATION_STATUSES.map((st) => [
			st,
			convocations.filter(
				(c) => (c.status || "").toLowerCase() === st.toLowerCase(),
			),
		]),
	);
	const lineup = (match?.matchPlayers || []).filter((p) => ours(p));
	const starters = lineup.filter(
		(p) => !p.provisional && p.startingPlayer === true,
	);
	const bench = lineup.filter(
		(p) => !p.provisional && p.startingPlayer === false,
	);

	// Momenti chiave (entrambe le squadre)
	const events = (match?.matchEvents || [])
		.filter((e) => KEY_EVENTS[e.eventType])
		.sort(compareMatchEvents);
	const eventName = (e) =>
		(match?.matchPlayers || []).find((p) => p.id === e.scorerParticipationId)
			?.playerName ||
		e.playerName ||
		(e.player ? playerName(e.player) : null) ||
		(e.playerId && playersById.get(e.playerId) && nameOf(e.playerId)) ||
		e.opponentName ||
		(e.opponentNumber ? "n° " + e.opponentNumber : "Giocatore");

	// Prestazioni individuali
	const statsRows = (detail?.stats || []).filter(ours);
	const statisticsByParticipation = new Map(
		statsRows.map((row) => [row.participationId, row]),
	);
	const disputed = statsRows.some(
		(row) =>
			row.warning || Object.values(row.metrics || {}).some((m) => m.conflict),
	);
	const unresolved = [
		...(match?.matchPlayers || []),
		...(match?.matchConvocations || []),
		...(detail?.stats || []),
	].filter((row) => !rowSide(row));
	const participants = [
		...lineup,
		...statsRows
			.filter((row) => !lineup.some((p) => p.id === row.participationId))
			.map((row) => ({
				id: row.participationId,
				playerId: row.playerId,
				playerName: row.playerName,
				statisticsOnly: true,
			})),
	];
	// Voti e contributi solo da valori certi: niente provvisori né conflitti
	const known = (row, key) => {
		const value = row?.metrics?.[key]?.conflict ? null : metric(row, key);
		return typeof value === "number" && Number.isFinite(value) ? value : null;
	};
	const performers = participants
		.filter((p) => !p.provisional && statisticsByParticipation.has(p.id))
		.map((p) => ({p, row: statisticsByParticipation.get(p.id)}));
	const rated = performers
		.filter(({row}) => known(row, "rating") !== null)
		.sort((a, b) => known(b.row, "rating") - known(a.row, "rating"));
	const score = ({row}) =>
		(known(row, "goals") || 0) * 2 + (known(row, "assists") || 0);
	const contributors = performers
		.filter((x) => score(x) > 0)
		.sort((a, b) => score(b) - score(a));
	const participantName = (p) =>
		p.playerName ||
		(p.player && playerName(p.player)) ||
		nameOf(p.playerId, "Nome non indicato");
	const present = byStatus.Presente.length;
	const eligible = lineup.filter((p) => !p.provisional);
	const expected = eligible.length;
	const metricTotals = [
		["totalShots", "Tiri totali rilevati"],
		["shotsOnTarget", "Tiri in porta rilevati"],
	].map(([key, label]) => {
		const values = eligible
			.map((p) => {
				const rows = statsRows.filter((row) => row.participationId === p.id);
				return rows.length === 1 ? metric(rows[0], key) : null;
			})
			.filter((v) => typeof v === "number" && Number.isFinite(v));
		return {
			label,
			value: values.length ? values.reduce((a, b) => a + b, 0) : null,
			known: values.length,
		};
	});
	const xgTotals = [
		["For", "xG a favore"],
		["Against", "xG contro"],
	].map(([suffix, label]) => {
		const values = ["1stHalf", "2ndHalf"]
			.map((half) => match?.matchIndexes?.[`xg${suffix}${half}`])
			.filter((value) => typeof value === "number" && Number.isFinite(value));
		return {
			label,
			known: values.length,
			value: values.length ? values.reduce((a, b) => a + b, 0) : null,
		};
	});
	const measurementSide = (row) => {
		const explicit = rowSide(row);
		if (explicit) return explicit;
		const choices = sides.filter((value) =>
			(match?.matchPlayers || []).some(
				(p) =>
					!p.provisional && p.playerId === row.playerId && rowSide(p) === value,
			),
		);
		return choices.length === 1 ? choices[0] : null;
	};
	const rpeRows = (match?.matchRpe || []).filter(
		(r) => r.playerId && measurementSide(r) === side && side,
	);
	const rpeGroups = new Map();
	rpeRows.forEach((r) => {
		if (!rpeGroups.has(r.playerId)) rpeGroups.set(r.playerId, []);
		rpeGroups.get(r.playerId).push(r);
	});
	const rpeValues = [...rpeGroups.values()]
		.filter((rows) => rows.length === 1)
		.map(([r]) => parseFloat(String(r.rpe).trim().replace(",", ".")))
		.filter((v) => Number.isFinite(v) && v >= 1 && v <= 10);
	const gpsRows = (match?.matchGpsStats || []).filter(
		(r) => r.playerId && measurementSide(r) === side && side,
	);
	const gpsByPlayer = new Map();
	gpsRows.forEach((r) => {
		if (!gpsByPlayer.has(r.playerId)) gpsByPlayer.set(r.playerId, []);
		gpsByPlayer.get(r.playerId).push(r);
	});
	const gpsMetrics = [...gpsByPlayer.values()].map(
		(rows) => summarizeDsGps(rows).metrics,
	);
	const gpsValues = (key) =>
		gpsMetrics
			.map((metrics) => metrics.find((m) => m.key === key)?.mean)
			.filter((v) => v != null);
	const distances = gpsValues("totalDistance"),
		speeds = gpsValues("maxSpeed");
	const measurementsUnassigned = [
		...(match?.matchRpe || []),
		...(match?.matchGpsStats || []),
	].filter((r) => !measurementSide(r)).length;

	// Analisi e statistiche di squadra
	const analysis = detail?.analysis;
	const hasAnalysis =
		analysis &&
		(analysis.generalTeamReport ||
			analysis.ourTacticalSystem1stHalf ||
			analysis.ourNotes1stHalf);
	const notes = (detail?.notes || []).filter((n) => n.note);

	const sheetState = (() => {
		if (!match || !side) return null;
		const complete =
			match[side + "ParticipationComplete"] &&
			match[side + "ScoringComplete"] &&
			match[side + "DisciplineComplete"];
		if (
			complete &&
			!disputed &&
			!unresolved.length &&
			!detail?.statsError &&
			!lineup.some((p) => p.provisional) &&
			!(match.matchEvents || []).some((e) => e.validationWarning || !rowSide(e))
		)
			return {status: "ok", label: "Distinta completa"};
		if (lineup.length || events.length || match[side + "ResultGoalsComplete"])
			return {status: "warn", label: "Dati parziali"};
		return {status: "alert", label: "Distinta da compilare"};
	})();

	const missing = !side
		? []
		: isPast
			? [
					!lineup.length && {
						label: "Distinta e formazione",
						owner: "Team manager",
					},
					!events.length &&
						hasResult &&
						homeGoals + awayGoals > 0 && {
							label: "Marcatori",
							owner: "Staff tecnico",
						},
					!detail?.statsError &&
						!statsRows.length && {
							label: "Statistiche giocatori",
							owner: "Match analyst",
						},
					!detail?.analysisError &&
						!hasAnalysis && {
							label: "Analisi della gara",
							owner: "Staff tecnico",
						},
				].filter(Boolean)
			: [
					!convocations.length && {
						label: "Convocazioni",
						owner: "Team manager",
					},
					match?.startTimeToBeConfirmed && {
						label: "Orario",
						owner: "Team manager",
					},
					side === "home" &&
						!match?.fieldName &&
						!match?.venueId && {label: "Campo", owner: "Team manager"},
				].filter(Boolean);

	const ready = data && !snapshotError && !detailLoading;

	return (
		<Page>
			<div className="ds">
				{snapshotError && (
					<DsEmpty>Dati DS non disponibili o accesso non consentito.</DsEmpty>
				)}
				<DsSkeleton visible={snapshotLoading || detailLoading} sections={1} />
				{ready && !match && (
					<DsEmpty>Dati gara non disponibili o accesso non consentito.</DsEmpty>
				)}
				{ready && match && (
					<>
						<div className="ds-score">
							<div className="ds-score__meta">
								{[
									matchDateLabel(match, "dddd D MMMM"),
									match.competitionName || match.matchType,
									match.competitionSeasonGroupRoundName &&
										(isNaN(match.competitionSeasonGroupRoundName)
											? match.competitionSeasonGroupRoundName
											: "Giornata " + match.competitionSeasonGroupRoundName),
									match.fieldName,
									team?.name,
								]
									.filter(Boolean)
									.join(" · ")}
							</div>
							<div className="ds-score__line">
								<span className="ds-score__team">
									{match.homeTeamName || match.homeClub}
								</span>
								<span className="ds-score__result">
									{hasResult ? `${homeGoals} – ${awayGoals}` : "vs"}
								</span>
								<span className="ds-score__team">
									{match.awayTeamName || match.awayClub}
								</span>
							</div>
							{resultWarning && (
								<div className="ds-missing" role="status">
									{resultWarning}
								</div>
							)}
							<div className="ds-score__actions">
								<DsStatus
									status={
										resultWarning
											? "warn"
											: isPast
												? hasResult
													? "ok"
													: "warn"
												: "none"
									}
									label={
										resultWarning
											? "Risultato da verificare"
											: isPast
												? hasResult
													? "Risultato " +
														(RESULT_SOURCE[match.result?.source] || "inserito")
													: "Risultato non disponibile"
												: matchCountdown(match)
									}
								/>
								{isPast && sheetState && (
									<DsStatus
										status={sheetState.status}
										label={sheetState.label}
									/>
								)}
								<button
									className="ds-btn ds-btn--ghost"
									onClick={() => navigate("/matches/" + match.id)}
								>
									<OpenInNewIcon fontSize="small" /> Gara completa
								</button>
							</div>
						</div>

						<div
							className="ds-toolbar"
							role="group"
							aria-label="Lato della sintesi"
						>
							<DsChips
								options={sides.map((value) => ({
									value,
									label: sideLabel(value),
								}))}
								value={side || ""}
								onChange={setSelectedSide}
							/>
						</div>
						{!side && (
							<DsEmpty>
								{sides.length
									? "Seleziona un lato autorizzato per consultare partecipanti e statistiche."
									: "Nessun lato della distinta autorizzato."}
							</DsEmpty>
						)}
						{detail.statsError && (
							<DsEmpty>
								Statistiche giocatori non disponibili o accesso non consentito.
							</DsEmpty>
						)}
						{(detail.notesError || detail.analysisError) && (
							<DsEmpty>
								Alcuni dati dello staff non sono disponibili o non sono
								autorizzati.
							</DsEmpty>
						)}
						{unresolved.length > 0 && (
							<div role="status">
								{unresolved.length} voci con lato da verificare: non attribuite
								alla selezione.
							</div>
						)}
						{disputed && (
							<div role="status">
								Statistiche da verificare: i valori in conflitto non sono usati
								nella sintesi. Confrontali nella gara completa.
							</div>
						)}
						{side && (
							<DsSection
								title="Riepilogo del lato selezionato"
								hint="Somme dei valori rilevati; dati mancanti o in conflitto esclusi. Copertura sulla distinta non provvisoria, incluse le riserve. GPS totale prioritario o due tempi completi per persona; RPE duplicati esclusi."
							>
								<DsKpiRow>
									{metricTotals.map((m) => (
										<DsKpi
											key={m.label}
											label={m.label}
											value={detail.statsError ? "—" : fmt(m.value, 2)}
											sub={
												detail.statsError
													? "Dato non disponibile"
													: `${m.known}/${expected} partecipanti con dato · ${m.known < expected ? "totale parziale" : expected ? "copertura completa" : "distinta non disponibile"}`
											}
										/>
									))}
									<DsKpi
										label="RPE medio gara"
										value={
											rpeValues.length
												? fmt(
														rpeValues.reduce((a, b) => a + b, 0) /
															rpeValues.length,
														1,
													)
												: "—"
										}
										sub={`${rpeValues.length} giocatori con valore unico · ${expected} in distinta`}
									/>
									<DsKpi
										label="GPS distanza rilevata"
										value={
											distances.length
												? fmt(distances.reduce((a, b) => a + b, 0)) + " m"
												: "—"
										}
										sub={`${distances.length} giocatori con dato · ${expected} in distinta · somma rilevata`}
									/>
									<DsKpi
										label="GPS velocità di picco"
										value={
											speeds.length
												? fmt(Math.max(...speeds), 1) + " km/h"
												: "—"
										}
										sub={`${speeds.length} giocatori con dato · ${expected} in distinta`}
									/>
								</DsKpiRow>
								{measurementsUnassigned > 0 && (
									<p role="status">
										{measurementsUnassigned} rilevazioni senza lato univoco
										escluse.
									</p>
								)}
								<button
									className="ds-link"
									onClick={() => navigate("/matches/" + id)}
								>
									Dettaglio nella gara completa
								</button>
							</DsSection>
						)}

						<DsSection
							title="xG dalla vista completa"
							hint="A favore e contro come inseriti nelle statistiche avanzate. Il riferimento non viene riassegnato al lato casa/trasferta selezionato."
						>
							<DsKpiRow>
								{xgTotals.map((m) => (
									<DsKpi
										key={m.label}
										label={m.label}
										value={fmt(m.value, 2)}
										sub={`${m.known}/2 tempi rilevati · ${m.known === 2 ? "totale" : "totale parziale"}`}
									/>
								))}
							</DsKpiRow>
						</DsSection>
						<DsMissing items={missing} />

						<DsKpiRow>
							{convocations.length > 0 && (
								<DsKpi
									label="Convocati"
									value={byStatus.Convocato.length + present}
									sub={present + " presenti"}
								/>
							)}
						</DsKpiRow>

						<div className="ds-two-cols">
							{side && (
								<DsSection
									title="Partecipanti e impiego"
									hint={
										lineup.length +
										" in distinta · " +
										starters.length +
										" titolari · " +
										bench.length +
										" riserve"
									}
								>
									{!participants.length && (
										<DsEmpty>
											Nessun giocatore in distinta per questo lato.
										</DsEmpty>
									)}
									<div
										className="ds-list"
										role="list"
										aria-label="Partecipanti del lato selezionato"
									>
										{participants.map((p) => {
											const row = statisticsByParticipation.get(p.id);
											const minutes = p.provisional
												? null
												: row?.metrics?.minutes
													? metric(row, "minutes")
													: p.calculatedMinutes ?? metric(row, "minutes");
											return (
												<div
													key={p.id}
													className="ds-list__item"
													role="listitem"
												>
													<div className="ds-list__main">
														<div className="ds-list__title">
															{p.playerNumber != null && (
																<b>{p.playerNumber} · </b>
															)}
															{p.playerName ||
																(p.player && playerName(p.player)) ||
																nameOf(p.playerId, "Nome non indicato")}
														</div>
														<div className="ds-list__sub">
															{p.provisional
																? "Provvisorio · presenza e minuti da verificare"
																: p.statisticsOnly
																	? "Statistiche senza presenza in distinta"
																	: (p.startingPlayer === true
																			? "Titolare"
																			: p.startingPlayer === false
																				? "Riserva"
																				: "Ruolo in distinta non indicato") +
																		" · " +
																		(p.hasPlayed === true
																			? "Entrato"
																			: "Non entrato")}
														</div>
														<div className="ds-list__sub">
															Minuti: {fmt(minutes)}
															{row?.metrics?.minutes?.conflict &&
																" · Da confrontare"}
														</div>
														{row && !p.provisional && (
															<div className="ds-list__sub">
																{[
																	["goals", "Gol"],
																	["assists", "Assist"],
																	["yellowCards", "Gialli"],
																	["redCard", "Espulsioni"],
																	...(row.goalkeeper
																		? [
																				["goalsConceded", "Subiti"],
																				["saves", "Parate"],
																			]
																		: []),
																	["rating", "Voto"],
																]
																	.map(
																		([key, label]) =>
																			label +
																			": " +
																			(row.metrics?.[key]?.conflict
																				? "Da confrontare"
																				: fmt(
																						metric(row, key),
																						key === "rating" ? 1 : 0,
																					)),
																	)
																	.join(" · ")}
															</div>
														)}
														{row?.warning && (
															<div role="status">{row.warning}</div>
														)}
														{!row && !p.provisional && (
															<div className="ds-list__sub">
																Statistiche individuali non disponibili.
															</div>
														)}
													</div>
												</div>
											);
										})}
									</div>
								</DsSection>
							)}

							{(rated.length > 0 || contributors.length > 0) && (
								<DsSection title="Prestazioni individuali">
									<div className="ds-facts">
										{rated.slice(0, 3).map(({p, row}) => (
											<div key={p.id} className="ds-facts__row">
												<span>{participantName(p)}</span>
												<ScoreRing value={known(row, "rating")} size="small" />
											</div>
										))}
										{contributors.slice(0, 3).map(({p, row}) => (
											<div key={"c" + p.id} className="ds-facts__row">
												<span>{participantName(p)}</span>
												<b>
													{[
														known(row, "goals") && `${known(row, "goals")} gol`,
														known(row, "assists") &&
															`${known(row, "assists")} assist`,
													]
														.filter(Boolean)
														.join(" · ")}
												</b>
											</div>
										))}
									</div>
								</DsSection>
							)}

							{!isPast && convocations.length > 0 && (
								<DsSection title="Convocati">
									<div className="choice-chips">
										{[...byStatus.Convocato, ...byStatus.Presente].map((c) => (
											<span
												key={c.id || c.playerId}
												className="choice-chip choice-chip--static"
											>
												{nameOf(c.playerId, c.playerName)}
											</span>
										))}
									</div>
									{byStatus.Infortunato.length + byStatus.Assente.length >
										0 && (
										<div className="ds-muted ds-small mt-2">
											Non disponibili:{" "}
											{[...byStatus.Infortunato, ...byStatus.Assente]
												.map((c) => nameOf(c.playerId, c.playerName))
												.join(", ")}
										</div>
									)}
								</DsSection>
							)}

							{events.length > 0 && (
								<DsSection title="Momenti chiave">
									<div
										className="ds-list"
										role="list"
										aria-label="Eventi della gara"
									>
										{events.map((e, i) => (
											<div
												key={e.id || i}
												className="ds-list__item"
												role="listitem"
											>
												<span className="ds-list__aside ds-list__aside--minute">
													{matchEventTime(e)}
												</span>
												<div className="ds-list__main">
													<div className="ds-list__title">{eventName(e)}</div>
													<div className="ds-list__sub">
														{KEY_EVENTS[e.eventType]} ·{" "}
														{rowSide(e)
															? sideLabel(rowSide(e))
															: "Lato da verificare"}
														{e.validationWarning && (
															<span role="status">
																{" "}
																· {e.validationWarning}
															</span>
														)}
													</div>
												</div>
											</div>
										))}
									</div>
								</DsSection>
							)}

							{hasAnalysis && (
								<DsSection
									title="Analisi dello staff"
									hint="Testo della gara completa; non riassegnato al lato selezionato."
								>
									<div className="ds-facts">
										{(analysis.ourTacticalSystem1stHalf ||
											analysis.ourTacticalSystem2ndHalf) && (
											<div className="ds-facts__row">
												<span>Sistema «nostro» nella vista completa</span>
												<b>
													{[
														analysis.ourTacticalSystem1stHalf,
														analysis.ourTacticalSystem2ndHalf,
													]
														.filter(Boolean)
														.join(" → ")}
												</b>
											</div>
										)}
										{(analysis.opponentTacticalSystem1stHalf ||
											analysis.opponentTacticalSystem2ndHalf) && (
											<div className="ds-facts__row">
												<span>Avversario</span>
												<b>
													{[
														analysis.opponentTacticalSystem1stHalf,
														analysis.opponentTacticalSystem2ndHalf,
													]
														.filter(Boolean)
														.join(" → ")}
												</b>
											</div>
										)}
									</div>
									{analysis.generalTeamReport && (
										<p className="ds-quote">{analysis.generalTeamReport}</p>
									)}
								</DsSection>
							)}

							{notes.length > 0 && (
								<DsSection title="Note dello staff">
									<div className="ds-list">
										{notes.slice(0, 4).map((n, i) => (
											<div key={n.id || i} className="ds-list__item">
												<div className="ds-list__main">
													<div className="ds-list__title">{n.note}</div>
													<div className="ds-list__sub">
														{[
															n.playerId && nameOf(n.playerId),
															n.title && "di " + n.title,
														]
															.filter(Boolean)
															.join(" · ")}
													</div>
												</div>
											</div>
										))}
									</div>
								</DsSection>
							)}

							{(match.scoutingPlanned ||
								match.observedMatch ||
								(match.prospectsObserved || []).length > 0) && (
								<DsMatchScouting key={id + seasonId} match={match} />
							)}
						</div>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsMatchPage;
